import React, { useEffect, useState, useRef, useCallback } from "react";
import { ListGroup, Button, Badge } from "react-bootstrap";
import "./MessageList.css";
import { useWebSocket } from "Providers/WebSocketContext";
import { ReactComponent as BlueTicksIcn } from "Assets/Images/icn/BlueTicks.svg";
import { ReactComponent as SingleTickIcn } from "Assets/Images/icn/SingleTick.svg";
import moment from "moment";
import { DATE_FORMAT_TIME, getDateSeparatorLabel } from "helpers/common";
import TiptapViewer from "components/shared/TiptapViewer";

const MessageList = ({
  userId,
  conversation,
  practName,
  practId,
  messageListRef,
  messagesEndRef,
  onScroll,
  onLoadPrevious,
  showLoadMore,
  onEditMessage,
  onDeleteMessage,
  isLoading,
}) => {
  const { sendMessage } = useWebSocket();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [currentVisibleDate, setCurrentVisibleDate] = useState(null);

  const scrollCheckTimeoutRef = useRef(null);
  const messageRefs = useRef({});
  const markedReadIds = useRef(new Set());

  if (!conversation?.messages?.length) return null;

  const unreadCount = conversation.unread_count;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = (e) => {
    if (onScroll) onScroll(e);
    const el = e.target;
    clearTimeout(scrollCheckTimeoutRef.current);
    scrollCheckTimeoutRef.current = setTimeout(() => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
      setShowScrollButton(!nearBottom);
    }, 100);
  };

  const markAsRead = useCallback((msgIds) => {
    if (!msgIds.length || !sendMessage) return;
    sendMessage(
      {
        action: "markAsRead",
        receiver_id: userId,
        sender_id: conversation?.pract_id,
        conversation_id: conversation?.conversation_id,
        msg_ids: msgIds,
      },
      (ack) => console.log("msg mark view success", ack),
      (error) => console.log("msg mark view success", error)
    );
    msgIds.forEach((id) => markedReadIds.current.add(id));
  }, [sendMessage, conversation]);

  useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      const visibleEntries = entries.filter(entry => entry.isIntersecting);
      const visibleMsg = visibleEntries[0];
      if (visibleMsg) {
        const msgId = Number(visibleMsg.target.dataset.msgid);
        const msg = conversation.messages.find((m) => Number(m.id) === msgId);
        if (msg) {
          const dateLabel = getDateSeparatorLabel(msg.created_at);
          setCurrentVisibleDate(dateLabel);
        }
      }

      const newlyVisibleUnreadIds = visibleEntries
        .map((entry) => Number(entry.target.dataset.msgid))
        .filter((id) => {
          const msg = conversation.messages.find((m) => Number(m.id) === id);
          return msg && !msg.is_read && !markedReadIds.current.has(id) && Number(msg.sender_id) !== Number(userId);
        });

      if (newlyVisibleUnreadIds.length > 0) {
        markAsRead(newlyVisibleUnreadIds);
      }
    },
    {
      root: messageListRef.current,
      threshold: 0.5,
    }
  );

  const timer = setTimeout(() => {
    Object.entries(messageRefs.current).forEach(([id, el]) => {
      if (el) observer.observe(el);
    });
  }, 0);

  return () => {
    clearTimeout(timer);
    observer.disconnect();
  };
}, [conversation.messages, markAsRead, messageListRef.current]);



  // const handleEditMessage = (msgId) => {
  //   try {
  //     const payload = {
  //       messageId: msgId,
  //       values: {
  //         body: "new message"
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error editing message:", error);
  //   }
  // }

  return (
    <div className="message-list-wrapper" style={{ position: "relative" }}>
      <div
        ref={messageListRef}
        onScroll={handleScroll}
        style={{ minHeight: "200px", maxHeight: "350px", overflowY: "auto" }}
      >
        {!isLoading && showLoadMore && conversation.totalMessagesCount > conversation.messages.length && (
          <div className="text-center mt-1">
            <Button className="theme-clr text-underline" size="sm" onClick={onLoadPrevious} variant="transparent">
              Load Previous Messages
            </Button>
          </div>
        )}

        {currentVisibleDate && (
          <div className="sticky-date-header text-center bg-transparent">
            <span className="badge bg-secondary px-3 py-1 rounded-pill small shadow-sm">
              {currentVisibleDate}
            </span>
          </div>
        )}

        <ListGroup variant="flush">
          {conversation.messages.map((msg, idx) => {
            const isFromPract = Number(msg.sender_id) === Number(practId);
            const senderName = isFromPract ? practName : "You";
            const formattedTime = moment(msg.created_at).format("h:mm A");
            const previousMsg = conversation.messages[idx - 1];
            const isSameSenderAsPrevious =
              previousMsg && Number(previousMsg.sender_id) === Number(msg.sender_id);
            const deleteAllowed = msg.status !== "pending" && moment(msg.created_at).isAfter(moment().subtract(24, 'hours'));
            const editAllowed = msg.status !== "pending" && moment(msg.created_at).isAfter(moment().subtract(30, 'minutes'));

            const currentMsgDate = moment(msg.created_at).startOf("day");
            const previousMsgDate = previousMsg ? moment(previousMsg.created_at).startOf("day") : null;

            const isLastMessage = idx === 0;
            const shouldInsertDateSeparator = !previousMsgDate || !currentMsgDate.isSame(previousMsgDate);
            const showDateSeparator = shouldInsertDateSeparator && !(isLastMessage && !previousMsg);

            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && (
                  <div className="text-center my-2">
                    <span className="badge bg-secondary px-3 py-1 rounded-pill small">
                      {getDateSeparatorLabel(msg.created_at)}
                    </span>
                  </div>
                )}

                <ListGroup.Item
                  key={`msg-${msg.id}`}
                  ref={(el) => (messageRefs.current[msg.id] = el)}
                  data-msgid={msg.id}
                  className={`d-flex justify-content-between align-items-center message-row ${isFromPract ? "received-message" : ""} ${!isSameSenderAsPrevious ? 'pt-3' : ''}`}
                >
                  <div className="d-flex flex-column flex-grow-1">
                    {!isSameSenderAsPrevious && (
                      <p className="mb-2 sender-name text-muted">
                        <strong>{senderName}</strong>
                      </p>
                    )}
                    <div className="message-body-wrapper">
                      {msg?.is_deleted ? (
                        <p title={`Deleted at ${DATE_FORMAT_TIME(msg.deleted_at)}`} className="m-0 message-deleted text-italic text-muted">
                          This message has been deleted
                        </p>
                      ) : (
                        <>
                          <p className="m-0 message-content">
                            <TiptapViewer
                              content={msg.message}
                            />
                            {msg?.edited_at && (  
                              <span className="message-edited-icon" title={`Edited at ${DATE_FORMAT_TIME(msg.edited_at)}`}>(Edited)</span>
                            )}
                          </p>
                          {!isFromPract && (
                            <div className="message-toolbar">
                              {editAllowed && (
                                <Button
                                  variant="transparent"
                                  className="p-0 px-1"
                                  onClick={() => onEditMessage(msg)}
                                >
                                  <i className="fa fa-pencil" aria-hidden="true"></i>
                                </Button>
                              )}
                              {deleteAllowed && (
                                <Button
                                  variant="transparent"
                                  className="p-0 px-1"
                                  onClick={() => onDeleteMessage(msg.id)}
                                >
                                  <i className="fa fa-trash text-danger" aria-hidden="true"></i>
                                </Button>
                              )}
                            </div>
                          )}
                          <div className="message-metadata">
                            <div className="timestamp-on-hover">{formattedTime}</div>
                            {!isFromPract && (
                              <div className="message-status d-flex align-items-center gap-1">
                                {msg.status === "pending" ? (
                                  <i className="fa fa-clock-o text-muted" title="Sending..." />
                                ) : msg.status === "failed" ? (
                                  <i className="fa fa-exclamation-circle text-danger" title="Failed to send" />
                                ) : msg.is_read ? (
                                  <span title="Read">
                                    <BlueTicksIcn />
                                  </span>
                                ) : (
                                  <span title="Sent">
                                    <SingleTickIcn />
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </ListGroup.Item>
              </React.Fragment>
            );
          })}
        </ListGroup>
        <div ref={messagesEndRef} />
      </div>

      {(showScrollButton || unreadCount > 0) && (
        <div className="scroll-to-bottom-container" onClick={scrollToBottom}>
          <div className="scroll-to-bottom-button">
            <i className="fa fa-angle-down" aria-hidden="true"></i>
            {unreadCount > 0 && (
              <Badge bg="danger" className="scroll-to-bottom-badge" pill>
                {unreadCount}
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;
